/**
 * rev 17b — the page-scoped multi-select action bar (PoolUsersPage). Renders ONLY when `count > 0`
 * (DESIGN.md §6, "nothing appears without a reason to look at").
 *
 * R1 ceremony/honesty pass: the two actions are NOT a matched red pair any more. Both are destructive,
 * but they differ in blast radius, so they now differ in weight — "Remove from account" (eject/unlink,
 * the heavier loss) is the SOLID danger button, "Unassign license" (user stays, drops to Basic) is a
 * lighter TINTED danger. A hairline divider separates them so the eye reads two distinct choices, not
 * one red block to click through. And each button's consequence is now visible as a subtitle UNDER its
 * label (was tooltip-only — an operator on touch/keyboard never saw it) rather than hidden in `title`.
 */
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { Button } from "@/components/ui";

export interface BulkForceBarProps {
  count: number;
  onClear: () => void;
  onSetPeriod: () => void;
  onLicenseRemove: () => void;
  onUnlink: () => void;
}

export function BulkForceBar({
  count,
  onClear,
  onSetPeriod,
  onLicenseRemove,
  onUnlink,
}: BulkForceBarProps) {
  const { t } = useTranslation();
  if (count === 0) return null;
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-(--radius) border border-(--border) bg-(--surface-sunken) px-4 py-2.5">
      <div className="flex items-center gap-2">
        <span className="text-(length:--text-sm) font-medium text-(--text)">
          {t("pool.users.bulk.selectedCount", { count })}
        </span>
        <Button variant="ghost" size="sm" onClick={onClear}>
          <X className="size-3.5" aria-hidden />
          {t("pool.users.bulk.clearSelection")}
        </Button>
      </div>
      <div className="flex flex-wrap items-stretch gap-3">
        {/* CONSTRUCTIVE, primary-filled, LEFTMOST: the common backfill action gets visual primacy AND
            maximum physical distance from the solid-danger unlink at the far right (misclick guard, DA#5).
            Reads as categorically different — a primary fill, not "a third shade of red". Same two-line
            label/subtitle stack the danger pair uses. */}
        <Button
          variant="primary"
          size="sm"
          onClick={onSetPeriod}
          className="h-auto flex-col items-start gap-0.5 py-1.5"
        >
          <span className="font-semibold">{t("pool.users.bulkSetPeriod.barLabel")}</span>
          <span className="text-(length:--text-2xs) font-normal opacity-90">
            {t("pool.users.bulkSetPeriod.barSubtitle")}
          </span>
        </Button>

        {/* Hairline divider — puts distance between the safe action and the danger pair. */}
        <div className="w-px self-stretch bg-(--border-strong)" aria-hidden />

        {/* Lighter TINTED danger: the user stays on the account (just drops to Basic) — the smaller
            loss, so the smaller visual weight. The className overrides ride on top of `secondary` via
            cn()/tailwind-merge (same in-repo precedent as PoolSettingsPage's ghost + text-(--danger)
            Remove). h-auto + py so the two-line label/subtitle stack fits the sm control. */}
        <Button
          variant="secondary"
          size="sm"
          onClick={onLicenseRemove}
          className="h-auto flex-col items-start gap-0.5 border-(--danger-subtle) bg-(--danger-subtle) py-1.5 text-(--danger-strong-fg) hover:border-(--danger) hover:bg-(--danger-subtle) hover:brightness-95"
        >
          <span className="font-semibold">{t("pool.users.bulk.licenseRemoveLabel")}</span>
          <span className="text-(length:--text-2xs) font-normal">
            {t("pool.users.bulk.licenseRemoveSubtitle")}
          </span>
        </Button>

        {/* Hairline divider — makes the pair read as two distinct choices, not one red block. */}
        <div className="w-px self-stretch bg-(--border-strong)" aria-hidden />

        {/* Heavier SOLID danger: eject from the Zoom account (unlink) — the larger, harder-to-undo
            loss, so it carries the full danger fill. */}
        <Button
          variant="danger"
          size="sm"
          onClick={onUnlink}
          className="h-auto flex-col items-start gap-0.5 py-1.5"
        >
          <span className="font-semibold">{t("pool.users.bulk.unlinkLabel")}</span>
          <span className="text-(length:--text-2xs) font-normal opacity-90">
            {t("pool.users.bulk.unlinkSubtitle")}
          </span>
        </Button>
      </div>
    </div>
  );
}
